/* ============================================================================
   SVNTY — COLLECTION (THE TYPE RAIL + THE RESULTS BLOCK + THE TAB RAIL + load on scroll + a fresh header offset)
   2026-09-19, dev1.0; R189 THE COLLECTION PAGE, 2026-09-25, dev1.1. Deferred, no dependencies.

   R189 (the reference's iPhone collection page): the next page loads ON SCROLL behind a spinner
   (`autoLoad`, an IntersectionObserver on the load-more block, the button kept as the retry after a stall);
   THE TYPE RAIL's indicator (`typeRail`: the pill is the visible share of the rail; the engine moves it on a
   scroll-driven animation, this script only where there is none); THE RESULTS BLOCK (`resultsDock`: is-docked
   flips when the sentinel crosses the header slab; the direction is the headstack's html.svnty-bar-hidden);
   THE TAB RAIL (`tabsRail`: the current tab centred whenever the rail shows, the fades where the engine has no
   scroll timeline). The next page is fetched through Shopify's
   Section Rendering API — exactly the endpoint Trade's own facets.js uses:

       /<collection path>?<current filter+sort params>&page=<N>&section_id=<product grid section id>

   The section id comes off the wrapper (`data-section-id`), which the section itself printed,
   so it survives a filter change (facets.js re-renders the whole #ProductGridContainer, wrapper
   included, back at page 1). The click handler is delegated on document for that reason — there
   is nothing to re-initialise after a re-render.

   THE URL IS NEVER REWRITTEN (M7). It used to be pushed to `?page=N`, which made a reload or a
   shared link show ONLY that page's 24 products — the shopper's 72 became 24 on refresh. Nothing
   is pushed now: the address bar keeps the filter/sort state the shopper actually chose, and a
   reload gives page 1 plus the same LOAD MORE path back.

   Nothing here writes to the sticky filter row's layer (iOS laws IOS-01/02/03); the one length it
   writes is `--header-height` on the ROOT element, and never from a scroll handler (M1).

   WORDS (H4): every sentence comes from `window.svntyStrings`, rendered by Liquid from
   `locales/*.json` in snippets/svnty-head.liquid. No English literal lives in this file.
   ============================================================================ */
(function () {
  'use strict';

  var STR = window.svntyStrings || {};

  document.documentElement.classList.add('svnty-js');

  function fill(key, tokens) {
    var out = STR[key] || '';
    for (var token in tokens) {
      if (Object.prototype.hasOwnProperty.call(tokens, token)) {
        out = out.replace(token, String(tokens[token]));
      }
    }
    return out;
  }

  /* ---------------------------------------------------------------------
     M1 — THE STICKY OFFSET STAYS TRUE

     The chip row pins at `top: var(--header-height)`. Trade writes that variable ONCE, in
     StickyHeader.connectedCallback, and again only on the 990px media change. Its own
     `reduce-logo-size` mode then shrinks the logo (and with it the header) on the first scroll,
     so from that moment the variable is TALLER than the header and page content scrolls through
     the gap under the pinned row.

     Re-measured here, and deliberately NOT from a scroll handler: a scroll-driven write to a
     length a sticky element depends on is the iOS trap (IOS-01/02/03 — WebKit's scrolling thread
     reconciles main-thread writes only when the gesture ends). Two observers instead, both of
     which fire when the header ACTUALLY changes, a handful of times per page:
       · ResizeObserver on `.section-header` — catches the logo/padding transition as it settles.
       · MutationObserver on its class list — catches the `scrolled-past-header` toggle itself,
         so the offset is right at the start of the transition as well as at its end.
     Trade's own `setHeaderHeight()` does the writing wherever the element has upgraded, so the
     variable keeps exactly one owner; the direct write is only the pre-upgrade fallback.
     The write target is `document.documentElement`, never the sticky row's own layer.
     --------------------------------------------------------------------- */
  function keepHeaderHeightTrue() {
    if (!document.querySelector('.facets-container')) return;

    var header = document.querySelector('.section-header');
    if (!header) return;
    var stickyHeader = document.querySelector('sticky-header');
    var last = -1;

    function measure() {
      var height = header.offsetHeight;
      if (!height || height === last) return;
      last = height;
      if (stickyHeader && typeof stickyHeader.setHeaderHeight === 'function') {
        stickyHeader.setHeaderHeight();
        return;
      }
      document.documentElement.style.setProperty('--header-height', height + 'px');
    }

    function schedule() {
      requestAnimationFrame(measure);
    }

    measure();

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(schedule).observe(header);
    }
    if (typeof MutationObserver === 'function') {
      new MutationObserver(schedule).observe(header, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
    window.addEventListener('pageshow', schedule);
  }

  /* ---------------------------------------------------------------------
     LOAD MORE
     --------------------------------------------------------------------- */
  function label(shown, total) {
    return fill('load_more', { '[shown]': shown, '[total]': total });
  }

  function finish(button, done, total) {
    button.hidden = true;
    done.textContent = fill('all_shown', { '[total]': total });
    done.hidden = false;
  }

  /* M9 — the honest stall. The paginator promised another page and the answer carried no cards,
     or the request failed outright. Never "All 24 shown" over a catalogue of 137: print what is
     really on screen out of the real total, leave the button in place and label it as a retry. */
  function stall(button, text, done, shown, total, reason) {
    var line = fill('shown_of_total', { '[shown]': shown, '[total]': total });
    if (done) {
      done.textContent = reason ? reason + ' ' + line : line;
      done.hidden = false;
    }
    if (text) text.textContent = STR.retry || '';
    button.hidden = false;
  }

  /* The section-rendering answer for `#product-grid` is NOT a bare list of cards: Trade prints a
     `.loading-overlay` div inside the grid, and on a page past the end it prints a
     "No products found" `.title-wrapper` there instead. Taking `children` wholesale appended a
     duplicate overlay on every press and made `items.length` non-zero even when the answer carried
     no products at all — which is exactly the state M9 has to detect. Cards only, therefore: Trade's
     grid items are the `<li>` children. */
  /* R3-3/R3-2 (round 3): `#product-grid` is NOT always the list. On /collections the id sits on
     the <ul> (main-collection-product-grid.liquid:147), but on /search it sits on the wrapping
     <div> (main-search.liquid:374) whose children are `.loading-overlay` + the <ul>. Reading
     `grid.children` for <li> therefore found ZERO cards on /search, every LOAD MORE press fell
     into stall() and a phone shopper could never reach results 25+. Find the list, then the
     cards — and append into the list, never into the wrapper. */
  function listIn(grid) {
    if (!grid) return null;
    if (grid.tagName === 'UL' || grid.tagName === 'OL') return grid;
    return grid.querySelector('ul.product-grid, ol.product-grid, ul.grid, ol.grid, ul, ol');
  }

  function cardsIn(grid) {
    var list = listIn(grid);
    if (!list) return [];
    return Array.prototype.filter.call(list.children, function (node) {
      return node.tagName === 'LI';
    });
  }

  function loadMore(button, auto) {
    var wrapper = button.closest('[data-svnty-load-more]');
    var grid = document.getElementById('product-grid');
    if (!wrapper || !grid || wrapper.dataset.busy === 'true') return;

    var sectionId = wrapper.dataset.sectionId;
    var totalPages = parseInt(wrapper.dataset.totalPages, 10);
    var total = parseInt(wrapper.dataset.total, 10);
    var shown = parseInt(wrapper.dataset.shown, 10);
    var next = parseInt(wrapper.dataset.nextPage, 10);
    var text = wrapper.querySelector('[data-svnty-load-more-label]');
    var done = wrapper.querySelector('[data-svnty-load-more-done]');
    var status = wrapper.querySelector('[data-svnty-load-more-status]');
    if (!isFinite(shown)) shown = cardsIn(grid).length;
    if (!isFinite(total)) total = shown;

    if (!sectionId || !isFinite(next) || !isFinite(totalPages) || next > totalPages) {
      finish(button, done, total);
      return;
    }

    var params = new URLSearchParams(window.location.search);
    params.delete('section_id');
    params.set('page', String(next));

    wrapper.dataset.busy = 'true';
    wrapper.classList.add('is-loading');
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    if (auto && status) status.textContent = STR.loading_more || '';

    fetch(window.location.pathname + '?' + params.toString() + '&section_id=' + encodeURIComponent(sectionId))
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var source = parsed.getElementById('product-grid');
        var items = cardsIn(source);

        if (!items.length) {
          wrapper.dataset.auto = 'off';
          stall(button, text, done, shown, total);
          return;
        }

        items.forEach(function (item) {
          // The reveal-on-scroll observer only runs over the initial markup; appended cards
          // must render immediately rather than sit at opacity 0.
          item.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
          item.removeAttribute('data-cascade');
          item.removeAttribute('style');
          (listIn(grid) || grid).appendChild(item);
        });

        /* D6-15 (collection_guard CL51): THE ROW LAW levels the appended rows in this task, before they paint (the rows'
           observer levelled them a frame late: an appended row stood 90 px tall for a frame at 393) */
        if (window.SVNTY && window.SVNTY.rows) window.SVNTY.rows.measure();

        shown = shown + items.length;
        next = next + 1;
        wrapper.dataset.shown = String(shown);
        wrapper.dataset.nextPage = String(next);

        if (text) text.textContent = label(shown, total);
        if (done) done.hidden = true;

        /* M8 — focus is NOT moved. The button the shopper pressed is still the focused element and
           it travels down with the grid, so the next press needs no navigation at all. The new
           count is announced through the polite live region instead. */
        if (status) status.textContent = fill('shown_of_total', { '[shown]': shown, '[total]': total });

        if (next > totalPages) {
          finish(button, done, total);
          if (done && document.activeElement === button) {
            done.setAttribute('tabindex', '-1');
            try {
              done.focus({ preventScroll: true });
            } catch (error) {
              done.focus();
            }
          }
        }
      })
      .catch(function () {
        wrapper.dataset.auto = 'off';
        stall(button, text, done, shown, total, STR.load_error || '');
      })
      .then(function () {
        wrapper.dataset.busy = 'false';
        wrapper.classList.remove('is-loading');
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        wrapper.dispatchEvent(new CustomEvent('svnty:loadmore', { bubbles: true }));
      });
  }

  /* ---------------------------------------------------------------------
     R189 — LOAD ON SCROLL. The block is watched 640 px before it enters; each arrival asks for the next page
     behind the spinner; a stall or an error hands the block back to the button (a retry the shopper presses).
     Trade's facets.js replaces #ProductGridContainer on every filter change, so the watch is re-armed on the
     new block from a MutationObserver on the container.
     --------------------------------------------------------------------- */
  function autoLoad() {
    if (!('IntersectionObserver' in window)) return;
    var io = null;
    var watched = null;

    function arm() {
      var wrapper = document.querySelector('[data-svnty-load-more]');
      if (wrapper === watched) return;
      if (io) io.disconnect();
      watched = wrapper;
      if (!wrapper) return;
      var next = parseInt(wrapper.dataset.nextPage, 10);
      var pages = parseInt(wrapper.dataset.totalPages, 10);
      if (!isFinite(next) || !isFinite(pages) || next > pages) return;
      wrapper.dataset.auto = 'on';
      io = new IntersectionObserver(function (entries) {
        var near = entries.some(function (e) { return e.isIntersecting; });
        if (!near) return;
        if (wrapper.dataset.auto !== 'on' || wrapper.dataset.busy === 'true') return;
        var n = parseInt(wrapper.dataset.nextPage, 10);
        var p = parseInt(wrapper.dataset.totalPages, 10);
        if (!isFinite(n) || n > p) { io.disconnect(); return; }
        var button = wrapper.querySelector('[data-svnty-load-more-button]');
        if (button) loadMore(button, true);
      }, { rootMargin: '1600px 0px 1000px 0px' });   /* D6-10 (collection_guard CL11, E-13): a fling to the bottom leaves the loader
                                                       above the screen behind the rails and the footer; its reach upward covers them */
      io.observe(wrapper);
      wrapper.addEventListener('svnty:loadmore', function () {
        /* still in reach after the append (a short page): look again */
        if (!io) return;
        io.unobserve(wrapper);
        io.observe(wrapper);
      });
    }

    arm();
    var container = document.getElementById('ProductGridContainer');
    if (container && typeof MutationObserver === 'function') {
      new MutationObserver(function () { requestAnimationFrame(arm); }).observe(container, { childList: true, subtree: true });
    }
  }

  /* =====================================================================
     THE DOCK v3 (the over-pull, the shoot). R195, R197; 2026-09-25 16:54, 17:51, 18:50, 18:55, 19:15.
     The page indicator under THE TYPE RAIL, self-contained for the asset kit: `dockV3(rail)` returns
     { measure, destroy }; its styles are the "THE DOCK v3" block of svnty-collection.css (tokens first), its markup
     the dots in snippets/svnty-type-rail.liquid. Brief: 03_design/research/motion_2026-09-25/
     MOTION_RIBBON_OVERPULL_BRIEF_2026-09-25.md sections 0 to 3 and 6.

     Pieces per slot, all pre-rendered, all compositor-only (transform, opacity):
       span.svnty-tr__dot    the group: translate (where the slot sits)
         i.f                 the fill: opacity (the ink, LOW to 1)
           b.c b.bar b.c.r   a cap, a bar stretched by scaleX (origin left), a cap slid by translate
         b.o                 the ring: the hollow dot (opacity; a counter-translate during the shoot)

     1. THE SCROLL (no stretch). One slot per REST STOP: every snap the rail can rest on, measured against the
        scroller's own scroll-padding (the snap line), a stop within MERGE px of the end folded into it, the last slot
        the end. The detent on segment k: f = k + (1 - cos(pi t)) / 2, zero slope at every stop. The raised-cosine
        kernel K(u) = (1 + cos(pi u / R)) / (2R) for |u| < R on an ENDLESS row (the virtual slots past both ends carry
        mass and are never drawn): X_i = PITCH i + E cum_i, ext_i = E K(f - i), ink_i = LOW + (1 - LOW) K(f - i) / K(0),
        E = PEAK / K(0). The lead is DOT + PEAK = 20 px at every stop; no slot outside the kernel's window ever moves.
        Keyframed ONCE per geometry on the rail's ScrollTimeline (axis inline, fill both); every effect carries offset
        0 and offset 1, so the engine never synthesises a neutral keyframe (the base style) at either end: that was
        D5-10's "only dots at rest" (its first keyframe sat at 4 / over, the list's ring room). SEG samples per segment
        inside each slot's own window: <= 31 keyframes per effect at any tile count. The inline style mirrors the state
        at every arm, so no frame can ever paint the base style; re-armed only when the geometry changes.
     2. THE PULL (the stretch d > 0: the rail's raw scrollLeft past its range, which Safari reports; a touch fallback
        through UIScrollView's rubber band where the engine never reports it). Every slot folds to a plain dot centred
        in the same box (X = PITCH j + E / 2) and goes HOLLOW: the fill to 0, the ring to 1, the end dot included.
        c = 1 - (1 - min(1, d / PULL))^2: no dead zone, fully hollow at PULL px. WAAPI overrides above the scroll effects,
        held at playbackRate 0 (never paused: Chromium does not paint a paused override over a composited scroll effect);
        each scroll event sets currentTime (<= 1 ms).
     3. THE SHOOT (the finger lifts and the stretch starts to fall; touchend on the fallback; from a pull of c >= SHOOT_MIN).
        The lead bar enters from beyond the row's edge on the pull side and lands on its end slot: at the start it flies
        in from the left onto slot 0, at the end from the right onto the last slot. Its leading edge rides the iOS spring
        (response LEAD[0] s, dampingFraction LEAD[1]), its trailing edge the critically damped spring of response TRAIL[0]:
        the bar streaks to ~25 px in flight, overshoots its 20 px once by length and settles without dipping below rest.
        The row slides home on the leading spring, the dots refill (ring out, fill in) over REFILL ms, the lead reaches
        full ink LEAD_IN ms in and its target ring (held still by a counter-translate) fades over RING_OUT as the bar
        lands. SHOOT_T ms, SHOOT_N + 1 keyframes at t = (k / N)^WARP (<= 0.07 px from the springs); the last keyframe IS
        the scroll state, then the overrides cancel: the hand-off is bit-exact. A lighter pull rides the stretch back
        (the brief's path); on the fallback that ride is a RIDE_T ms critically damped linear() spring (omega RIDE_W).
     Reduced motion: reach 1; the pull still follows the finger; no shoot: fill and ring cross-fade in FADE_T ms.
     RTL: the box is mirrored in CSS; the maths runs on the inline offset (-scrollLeft in RTL).
     ===================================================================== */
  var DOCK = {
    DOT: 4, GAP: 6, PEAK: 16, LOW: 0.18, MERGE: 32, SEG: 7, PULL: 28, PULL_N: 8,
    SHOOT_MIN: 0.95, SHOOT_T: 800, SHOOT_N: 40, WARP: 1.4, SHOOT_FROM: 32,
    LEAD: [0.45, 0.72], TRAIL: [0.45, 1], LEAD_IN: 120, REFILL: 240, RING_OUT: [120, 280],
    RIDE_T: 480, RIDE_W: 14, FADE_T: 200
  };

  function dockSpring(resp, zeta) {                /* the step response of a spring, 0 at t = 0, 1 at rest (t in s) */
    var w = 2 * Math.PI / resp;
    if (zeta < 1) {
      var wd = w * Math.sqrt(1 - zeta * zeta);
      return function (t) { return 1 - Math.exp(-zeta * w * t) * (Math.cos(wd * t) + zeta * w / wd * Math.sin(wd * t)); };
    }
    return function (t) { return 1 - (1 + w * t) * Math.exp(-w * t); };
  }
  function dockSmooth(u) { u = u < 0 ? 0 : u > 1 ? 1 : u; return u * u * (3 - 2 * u); }
  function dockCancel(list) { list.forEach(function (a) { try { a.cancel(); } catch (err) { /* gone */ } }); }

  function dockV3(rail) {
    var sc = rail.querySelector('[data-svnty-tr-scroller]');
    var box = rail.querySelector('[data-svnty-tr-dots]');
    if (!sc || !box) return null;
    var items = Array.prototype.slice.call(rail.querySelectorAll('.svnty-tr__item'));
    var parts = Array.prototype.slice.call(box.children).map(function (s) {
      return { g: s, f: s.querySelector('.f'), bar: s.querySelector('.bar'), r: s.querySelector('.r'), o: s.querySelector('.o') };
    }).filter(function (p) { return p.f && p.bar && p.r && p.o; });
    if (!parts.length) return null;

    var D = DOCK, PITCH = D.DOT + D.GAP;
    var TL = typeof ScrollTimeline === 'function';
    var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
    var R = 2, E = 32, W = 0, over = 0, cw = 0, S = [], M = 0, rtl = false, sig = '';
    var anims = [], pullKeys = {}, painting = 0, quiet = 0, off = [];
    /* phase: 0 rest, 1 THE PULL, 2 riding the stretch back, 3 THE SHOOT (or the ride / fade, time-based) */
    var P = { phase: 0, side: 0, d: 0, c: 0, falls: 0, ov: [], fx: [], reports: false, touching: false, touch: null };
    var SPRING = (function () {                    /* the fallback ride: critically damped, omega RIDE_W, sampled for linear() */
      var n = 24, T = D.RIDE_T / 1000, s = [];
      for (var k = 0; k <= n; k++) { var t = T * k / n; s.push((1 - (1 + D.RIDE_W * t) * Math.exp(-D.RIDE_W * t)).toFixed(4)); }
      s[n] = '1';
      return 'linear(' + s.join(', ') + ')';
    })();

    function K(u) { var a = Math.abs(u); return a >= R ? 0 : (1 + Math.cos(Math.PI * a / R)) / (2 * R); }
    function xi() { return rtl ? -sc.scrollLeft : sc.scrollLeft; }
    function clampX(x) { return x < 0 ? 0 : x > over ? over : x; }
    function fOf(x) {
      if (x <= 0) return 0;
      if (x >= over) return M - 1;
      for (var j = 0; j < M - 1; j++) if (x < S[j + 1]) {
        var t = (x - S[j]) / Math.max(1e-6, S[j + 1] - S[j]);
        return j + (1 - Math.cos(Math.PI * t)) / 2;
      }
      return M - 1;
    }
    function state(f) {                            /* the row at the fractional stop f: x, e (extra length), a (ink), h (ring), rx */
      var out = [], j, k, cum = 0;
      for (j = 1; j <= R; j++) cum += K(f + j);
      for (j = 0; j < M; j++) {
        k = K(f - j);
        out.push({ x: j * PITCH + E * cum, e: E * k, a: D.LOW + (1 - D.LOW) * k / K(0), h: 0, rx: 0 });
        cum += k;
      }
      return out;
    }
    function hollow() {                            /* THE PULL's end: plain hollow dots, centred in the same box */
      var out = [];
      for (var j = 0; j < M; j++) out.push({ x: j * PITCH + E / 2, e: 0, a: 0, h: 1, rx: 0 });
      return out;
    }
    function pullRow(side, c) {                    /* the row at pull amount c from the rest at that end */
      var a = state(side < 0 ? 0 : M - 1), b = hollow(), q = (1 - c) * (1 - c), out = [];
      for (var j = 0; j < M; j++) out.push({ x: a[j].x + (b[j].x - a[j].x) * c, e: a[j].e * (1 - c), a: a[j].a * q, h: 1 - q, rx: 0 });
      return out;
    }
    function kG(s) { return { transform: 'translate3d(' + s.x.toFixed(3) + 'px,0,0)' }; }
    function kF(s) { return { opacity: +s.a.toFixed(4) }; }
    function kB(s) { return { transform: 'scaleX(' + (s.e / E).toFixed(5) + ')' }; }
    function kR(s) { return { transform: 'translate3d(' + s.e.toFixed(3) + 'px,0,0)' }; }
    function kO(s) { return { transform: 'translate3d(' + s.rx.toFixed(3) + 'px,0,0)', opacity: +s.h.toFixed(4) }; }
    function write(st) {                           /* the inline style = the state now (the base style never shows) */
      for (var j = 0; j < M; j++) {
        var p = parts[j];
        p.g.style.transform = kG(st[j]).transform;
        p.f.style.opacity = String(kF(st[j]).opacity);
        p.bar.style.transform = kB(st[j]).transform;
        p.r.style.transform = kR(st[j]).transform;
      }
    }
    function points(i) {                           /* the scroll offsets that carry a keyframe for slot i: 0 and over always */
      var pts = [0], a = Math.max(0, i - R), b = Math.min(M - 1, i + R), j, s, x;
      for (j = a; j < b; j++) for (s = 0; s < D.SEG; s++) {
        x = S[j] + (S[j + 1] - S[j]) * s / D.SEG;
        if (x > pts[pts.length - 1]) pts.push(x);
      }
      if (S[b] > pts[pts.length - 1]) pts.push(S[b]);
      if (over > pts[pts.length - 1]) pts.push(over);
      return pts;
    }

    function geometry() {
      var cs = getComputedStyle(sc);
      rtl = cs.direction === 'rtl';
      R = reduceMQ.matches ? 1 : 2;
      E = D.PEAK / K(0);
      over = Math.max(0, sc.scrollWidth - sc.clientWidth);
      cw = sc.clientWidth;
      var snap = parseFloat(cs.scrollPaddingInlineStart);
      if (!isFinite(snap)) snap = parseFloat(rtl ? cs.scrollPaddingRight : cs.scrollPaddingLeft);
      if (!isFinite(snap)) snap = parseFloat(rtl ? cs.paddingRight : cs.paddingLeft) || 0;
      var r = sc.getBoundingClientRect(), x0 = xi(), st = [0];
      items.forEach(function (it) {
        var b = it.getBoundingClientRect();
        var s = (rtl ? r.right - b.right : b.left - r.left) + x0 - snap;
        if (s > st[st.length - 1] + 1 && s < over - D.MERGE) st.push(s);
      });
      st.push(over);
      while (st.length > parts.length && st.length > 2) st.splice(st.length - 2, 1);
      S = st;
      M = over > 1 ? S.length : 0;
      W = M ? M * D.DOT + (M - 1) * D.GAP + E : 0;
    }

    function arm() {
      stop();
      dockCancel(anims); anims = [];
      pullKeys = {};
      parts.forEach(function (p, j) { p.g.hidden = j >= M; });
      if (!M) return;
      box.style.setProperty('--svnty-tr-e', E + 'px');
      box.style.setProperty('--svnty-tr-dw', W + 'px');
      write(state(fOf(clampX(xi()))));
      [-1, 1].forEach(function (side) {            /* THE PULL's keyframes, both ends, ready before any gesture */
        var rows = [], k, j;
        for (k = 0; k <= D.PULL_N; k++) rows.push(pullRow(side, k / D.PULL_N));
        pullKeys[side] = parts.slice(0, M).map(function (p, i) {
          var G = [], F = [], B = [], Rr = [], O = [];
          for (j = 0; j < rows.length; j++) { var s = rows[j][i]; G.push(kG(s)); F.push(kF(s)); B.push(kB(s)); Rr.push(kR(s)); O.push(kO(s)); }
          return [G, F, B, Rr, O];
        });
      });
      if (!TL) return;
      try {
        var tl = new ScrollTimeline({ source: sc, axis: 'inline' }), o = { timeline: tl, fill: 'both' };
        for (var i = 0; i < M; i++) {
          var pts = points(i), G = [], F = [], B = [], Rr = [];
          for (var n = 0; n < pts.length; n++) {
            var s = state(fOf(pts[n]))[i], off0 = n === 0 ? 0 : n === pts.length - 1 ? 1 : pts[n] / over;
            var g = kG(s), f = kF(s), b = kB(s), r = kR(s);
            g.offset = f.offset = b.offset = r.offset = off0;
            G.push(g); F.push(f); B.push(b); Rr.push(r);
          }
          anims.push(parts[i].g.animate(G, o), parts[i].f.animate(F, o), parts[i].bar.animate(B, o), parts[i].r.animate(Rr, o));
        }
      } catch (err) {
        TL = false;
        dockCancel(anims); anims = [];
        write(state(fOf(clampX(xi()))));
      }
    }

    function measure(force) {
      geometry();
      var key = [M, R, rtl ? 1 : 0, over.toFixed(1)].concat(S.map(function (s) { return s.toFixed(1); })).join('|');
      if (!force && key === sig) return;
      sig = key;
      arm();
    }

    /* ---- THE PULL ---- */
    function dropPull() { if (P.ov.length) { dockCancel(P.ov); P.ov = []; } }
    function stop() { dropPull(); dockCancel(P.fx); P.fx = []; P.phase = 0; P.d = 0; P.c = 0; P.falls = 0; }
    function setPull(d, side) {
      if (!(d > 0.25) || !M) { dropPull(); P.d = 0; P.c = 0; return; }
      if (!P.ov.length || P.side !== side) {
        dropPull(); dockCancel(P.fx); P.fx = [];
        var keys = pullKeys[side], o = { duration: 1000, fill: 'both' };
        for (var j = 0; j < M; j++) {
          var p = parts[j], k = keys[j];
          P.ov.push(p.g.animate(k[0], o), p.f.animate(k[1], o), p.bar.animate(k[2], o), p.r.animate(k[3], o), p.o.animate(k[4], o));
        }
        P.ov.forEach(function (a) { a.playbackRate = 0; });
        P.side = side;
      }
      var u = Math.min(1, d / D.PULL);
      P.d = d; P.c = 1 - (1 - u) * (1 - u);
      var t = 1000 * P.c;
      for (var n = 0; n < P.ov.length; n++) P.ov[n].currentTime = t;
    }
    function play(list, done) {                    /* time-based overrides: cancel on finish, the scroll effects take over */
      P.fx = list; P.phase = 3;
      var last = list[list.length - 1];
      if (last) last.onfinish = function () { if (P.fx === list) { dockCancel(list); P.fx = []; P.phase = 0; if (done) done(); } };
    }

    /* ---- THE SHOOT ---- */
    function shootKeys(side, c0) {
      var lead = side < 0 ? 0 : M - 1, dir = side < 0 ? 1 : -1;
      var rest = state(lead), from = pullRow(side, c0), N = D.SHOOT_N, T = D.SHOOT_T / 1000;
      var head = dockSpring(D.LEAD[0], D.LEAD[1]), tail = dockSpring(D.TRAIL[0], D.TRAIL[1]);
      /* the lead's two edges in box px: the start side flies rightward (dir 1), the end side leftward (dir -1) */
      var rL = rest[lead], back0 = dir > 0 ? rL.x : rL.x + D.DOT + rL.e, front0 = dir > 0 ? rL.x + D.DOT + rL.e : rL.x;
      var launchBack = back0 - dir * D.SHOOT_FROM, launchFront = launchBack + dir * D.DOT;
      var ringAt = from[lead].x;                   /* the target ring stays where the pull left it */
      var keys = [], offs = [], k, j;
      for (j = 0; j < M; j++) keys.push([[], [], [], [], []]);
      for (k = 0; k <= N; k++) {
        var u = Math.pow(k / N, D.WARP), t = u * T, ms = t * 1000, end = k === N;
        var sh = end ? 1 : head(t), st = end ? 1 : tail(t), fill = dockSmooth(ms / D.REFILL);
        offs.push(end ? 1 : u);
        for (j = 0; j < M; j++) {
          var s;
          if (end) s = { x: rest[j].x, e: rest[j].e, a: rest[j].a, h: 0, rx: 0 };
          else if (j === lead) {
            var bk = launchBack + (back0 - launchBack) * st, fr = launchFront + (front0 - launchFront) * sh;
            var x = dir > 0 ? bk : fr, e = Math.max(0, Math.abs(fr - bk) - D.DOT);
            s = { x: x, e: e, a: rest[j].a * dockSmooth(ms / D.LEAD_IN),
              h: from[j].h * (1 - dockSmooth((ms - D.RING_OUT[0]) / (D.RING_OUT[1] - D.RING_OUT[0]))), rx: ringAt - x };
          } else {
            s = { x: from[j].x + (rest[j].x - from[j].x) * sh, e: from[j].e + (rest[j].e - from[j].e) * sh,
              a: from[j].a + (rest[j].a - from[j].a) * fill, h: from[j].h * (1 - fill), rx: 0 };
          }
          var q = [kG(s), kF(s), kB(s), kR(s), kO(s)];
          for (var m = 0; m < 5; m++) { q[m].offset = offs[k]; keys[j][m].push(q[m]); }
        }
      }
      return keys;
    }
    function shoot(side, c0) {
      var keys = shootKeys(side, c0), o = { duration: D.SHOOT_T, fill: 'both' }, list = [];
      dropPull(); dockCancel(P.fx);
      for (var j = 0; j < M; j++) {
        var p = parts[j], k = keys[j];
        list.push(p.g.animate(k[0], o), p.f.animate(k[1], o), p.bar.animate(k[2], o), p.r.animate(k[3], o), p.o.animate(k[4], o));
      }
      play(list);
    }
    function home(side, c0, fade) {                /* a lighter pull on the fallback: the spring ride; reduced motion: the fade */
      var a = state(side < 0 ? 0 : M - 1), m = pullRow(side, c0), list = [];
      dropPull(); dockCancel(P.fx);
      var o = fade ? { duration: D.FADE_T, easing: 'linear', fill: 'both' } : { duration: D.RIDE_T, easing: SPRING, fill: 'both' };
      for (var j = 0; j < M; j++) {
        var p = parts[j], z = { x: a[j].x, e: a[j].e, a: a[j].a, h: 0, rx: 0 };
        list.push(p.f.animate([kF(m[j]), kF(z)], o), p.o.animate([kO(m[j]), kO(z)], o));
        if (!fade) list.push(p.g.animate([kG(m[j]), kG(z)], o), p.bar.animate([kB(m[j]), kB(z)], o), p.r.animate([kR(m[j]), kR(z)], o));
      }
      play(list);
    }
    function release() {
      if (P.phase !== 1) return;
      var side = P.side, c = P.c;
      P.falls = 0;
      if (reduceMQ.matches) return home(side, c, true);
      if (c >= D.SHOOT_MIN) return shoot(side, c);
      if (P.reports) { P.phase = 2; return; }       /* ride the stretch back on the rail's own bounce */
      home(side, c, false);
    }

    /* ---- the signals ---- */
    function paint() {
      if (painting) return;
      painting = requestAnimationFrame(function () { painting = 0; write(state(fOf(clampX(xi())))); });
    }
    function settle() {                            /* scrollend (or 150 ms of quiet): nothing may stay folded */
      quiet = 0;
      if (P.touching) return;
      if (P.phase === 1) release();
      if (P.phase === 2) { setPull(0); P.phase = 0; }
    }
    function onScroll() {
      var x = xi();
      if (!P.reports && M && (x < -0.5 || x > over + 0.5)) P.reports = true;
      if (P.reports && M) {
        var side = x < 0 ? -1 : 1, d = x < 0 ? -x : Math.max(0, x - over);
        if (P.phase === 3 && P.touching && d > 0.25) { dockCancel(P.fx); P.fx = []; P.phase = 0; }
        if (P.phase === 2) { setPull(d, P.side); if (!(d > 0.25)) P.phase = 0; }
        else if (P.phase !== 3) {
          if (d > 0.25) {
            if (P.phase === 1 && !P.touching && d < P.d - 0.05 && side === P.side) {
              if (++P.falls >= 2) release(); else setPull(d, side);
            } else { P.falls = 0; P.phase = 1; setPull(d, side); }
          } else if (P.phase === 1) {
            release();
            if (P.phase === 2) { setPull(0); P.phase = 0; }
          }
        }
        if (P.phase === 1 || P.phase === 2) { clearTimeout(quiet); quiet = setTimeout(settle, 150); }
      }
      if (!TL) paint();
    }
    function rubber(x) { return (1 - 1 / (x * 0.55 / cw + 1)) * cw; }
    function onStart(e) {
      P.touching = true;
      if (P.reports || !M || !e.touches[0]) return;
      var x = xi();
      P.touch = { x0: e.touches[0].clientX, y0: e.touches[0].clientY, lock: 0, atStart: x <= 0.5, atEnd: x >= over - 0.5 };
    }
    function onMove(e) {
      if (P.reports || !P.touch || !e.touches[0]) return;
      var T = P.touch, dx = e.touches[0].clientX - T.x0, dy = e.touches[0].clientY - T.y0;
      if (!T.lock) { if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return; T.lock = Math.abs(dx) > Math.abs(dy) ? 1 : 2; }
      if (T.lock !== 1) return;
      if (rtl) dx = -dx;
      if (T.atStart && dx > 0) { P.phase = 1; setPull(rubber(dx), -1); }
      else if (T.atEnd && dx < 0) { P.phase = 1; setPull(rubber(-dx), 1); }
      else if (P.phase === 1) { setPull(0); P.phase = 0; }
    }
    function onEnd(e) {
      P.touching = !!(e.touches && e.touches.length);
      if (P.touching) return;
      if (!P.reports && P.phase === 1) release();
      P.touch = null;
    }
    function on(type, fn) { sc.addEventListener(type, fn, { passive: true }); off.push(function () { sc.removeEventListener(type, fn, { passive: true }); }); }
    on('scroll', onScroll);
    on('scrollend', settle);
    on('touchstart', onStart);
    on('touchmove', onMove);
    on('touchend', onEnd);
    on('touchcancel', onEnd);
    function onMotion() { measure(true); }
    if (reduceMQ.addEventListener) {
      reduceMQ.addEventListener('change', onMotion);
      off.push(function () { reduceMQ.removeEventListener('change', onMotion); });
    }

    /* the bench (the lead's replay and the owner's device probe): drives the same code paths as a gesture */
    rail._svntyDock = {
      info: function () { return { M: M, S: S.slice(), over: over, E: E, W: W, R: R, TL: TL, reports: P.reports, phase: P.phase, c: P.c }; },
      state: function (f) { return state(f); }, fOf: function (x) { return fOf(x); },
      hollow: function () { return hollow(); }, pullRow: function (side, c) { return pullRow(side, c); },
      pull: function (d, side) { P.phase = d > 0.25 ? 1 : 0; setPull(d, side); },
      release: function () { release(); },
      shootMs: D.SHOOT_T,
      busy: function () { return P.fx.length > 0 || P.ov.length > 0; }
    };

    return {
      measure: measure,
      destroy: function () {
        off.forEach(function (fn) { fn(); }); off = [];
        clearTimeout(quiet);
        if (painting) cancelAnimationFrame(painting);
        stop(); dockCancel(anims); anims = [];
        delete rail._svntyDock;
      }
    };
  }

  /* ---------------------------------------------------------------------
     R189 THE TYPE RAIL: data-tr="flat" when every tile fits (the dock hides); the current tile revealed on load;
     THE DOCK v3 above measured and re-armed on every change of geometry: the scroller's and the list's size (a picture
     that loads changes its tile's width), a picture's load, the window's resize and load, the editor's section load
     (boot re-runs this function; the previous run is torn down first).
     --------------------------------------------------------------------- */
  var typeRailKill = null;
  function typeRail() {
    if (typeRailKill) { typeRailKill(); typeRailKill = null; }
    var rail = document.querySelector('[data-svnty-type-rail]');
    if (!rail) return;
    var sc = rail.querySelector('[data-svnty-tr-scroller]');
    if (!sc) return;
    var list = rail.querySelector('.svnty-tr__list');
    var dock = dockV3(rail);
    var over = 0, frame = 0, ms = null, me = null, tr = null, off = [], ro = null;

    function ends() {
      var x = Math.abs(sc.scrollLeft), a = over > 1 && x > 1, b = over > 1 && x < over - 1;
      if (a !== ms) rail.setAttribute('data-more-start', (ms = a) ? 'true' : 'false');
      if (b !== me) rail.setAttribute('data-more-end', (me = b) ? 'true' : 'false');
    }
    function measure() {
      frame = 0;
      over = Math.max(0, sc.scrollWidth - sc.clientWidth);
      /* TASTE_CONSENT (CL20): written only on a change; every picture's load re-measures, and an unchanged write still
         restyled the rail inside the swipe */
      var t = over > 1 ? 'rail' : 'flat';
      if (t !== tr) rail.setAttribute('data-tr', (tr = t));
      if (dock) dock.measure();
      ends();
    }
    function schedule() { if (!frame) frame = requestAnimationFrame(measure); }
    function reveal() {
      var cur = rail.querySelector('.svnty-tr__item.is-current');
      if (!cur) return;
      var r = cur.getBoundingClientRect(), s = sc.getBoundingClientRect();
      var pad = parseFloat(getComputedStyle(sc).paddingLeft) || 0;
      if (r.right > s.right || r.left < s.left + pad) sc.scrollLeft = cur.offsetLeft - pad;
    }
    function on(t, type, fn, opt) { t.addEventListener(type, fn, opt); off.push(function () { t.removeEventListener(type, fn, opt); }); }
    function onLoad() { measure(); reveal(); }

    on(sc, 'scroll', ends, { passive: true });
    on(rail, 'load', schedule, true);
    on(window, 'resize', schedule, { passive: true });
    on(window, 'load', onLoad);
    if (typeof ResizeObserver === 'function') {
      ro = new ResizeObserver(schedule);
      ro.observe(sc);
      if (list) ro.observe(list);
    }
    typeRailKill = function () {
      off.forEach(function (fn) { fn(); });
      if (ro) ro.disconnect();
      if (frame) cancelAnimationFrame(frame);
      if (dock) dock.destroy();
    };
    measure();
    reveal();
  }

  /* ---------------------------------------------------------------------
     R189 — THE RESULTS BLOCK docks. is-docked = the sentinel (the block's place in the flow) has passed under the
     header slab's visible height (--svnty-headstack-h). Read on the scroll's frame, written only on a flip; the
     block's own layer takes no scroll-time write (the iOS law), the direction is the headstack's class.
     --------------------------------------------------------------------- */
  function resultsDock() {
    var wrap = document.querySelector('[data-svnty-results]');
    var sent = document.querySelector('[data-svnty-results-sentinel]');
    if (!wrap || !sent) return;
    var docked = null, ticking = false;
    var root = document.documentElement;

    function slabH() {
      var v = parseFloat(getComputedStyle(root).getPropertyValue('--svnty-headstack-h'));
      return isFinite(v) ? v : 98;
    }

    function check() {
      ticking = false;
      /* docked once the block's flow place is a whole rail height under the slab, so the step down for the rail never
         lands above the flow position (refuter E-04: a 38 px jump against the finger at the dock line) */
      /* D6-12 (collection_guard CL4): the slab shrinks by the bar's height at the very moment the block docks on a scroll
         down (the bar parks), which moved the dock line 34 px up and un-docked the block into a 44 px ease against the
         finger; once docked, the block stays docked until its flow place is back UNDER the slab's current bottom */
      var top = sent.getBoundingClientRect().top;
      /* on the way up the block lets go only when its flow place has caught up with where it is drawn (the docked block sits a
         rail height under the slab), so the sticky releases with no motion at all; on the way down it docks a whole rail
         height under the slab as before */
      var d = docked ? top < wrap.getBoundingClientRect().top - 0.5 : top <= slabH() - 44 + 0.5;
      /* D6-1 (refuter F-31), re-cut in D6-4: on a short collection the block's CONTAINER carries it off (the section ends above
         the slab), so it is not docked and the tab rail stays parked; read the container's bottom, never the block's top,
         which lags the slab while the header regrows on a scroll up */
      if (d && wrap.parentNode && wrap.parentNode.getBoundingClientRect().bottom < slabH() + wrap.offsetHeight - 1) d = false;
      if (d === docked) return;
      docked = d;
      wrap.classList.toggle('is-docked', d);
      wrap.dispatchEvent(new CustomEvent('svnty:docked', { detail: { docked: d } }));
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(check);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    if ('onscrollend' in window) window.addEventListener('scrollend', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('load', onScroll);
    window.addEventListener('pageshow', onScroll);
    /* the page above the block changes height (pictures, a description, a filter re-render): re-read */
    if (typeof ResizeObserver === 'function') new ResizeObserver(onScroll).observe(document.body);
    check();
  }

  /* ---------------------------------------------------------------------
     R189 — THE TAB RAIL. The current tab is centred in the rail each time the rail comes out (instant: the rail
     is under the header while it moves), and the fades follow the overflow where the engine has no scroll
     timeline (data-more-start / data-more-end, as the header's rail does).
     --------------------------------------------------------------------- */
  function tabsRail() {
    var rail = document.querySelector('[data-svnty-type-tabs]');
    if (!rail) return;
    var list = rail.querySelector('[data-svnty-tt-scroller]');
    var wrap = rail.closest('[data-svnty-results]');
    if (!list) return;
    function fades() {
      var over = list.scrollWidth - list.clientWidth;
      if (over <= 1) { rail.setAttribute('data-tt', 'flat'); return; }
      rail.setAttribute('data-tt', 'rail');
      rail.setAttribute('data-more-start', list.scrollLeft > 1 ? 'true' : 'false');
      rail.setAttribute('data-more-end', list.scrollLeft < over - 1 ? 'true' : 'false');
    }

    function centre() {
      var cur = rail.querySelector('.svnty-tt__item.is-current');
      if (!cur) return;
      var over = list.scrollWidth - list.clientWidth;
      if (over <= 1) return;
      var target = cur.offsetLeft + cur.offsetWidth / 2 - list.clientWidth / 2;
      list.scrollLeft = Math.max(0, Math.min(over, target));
      fades();
    }

    list.addEventListener('scroll', function () { requestAnimationFrame(fades); }, { passive: true });
    if (typeof ResizeObserver === 'function') new ResizeObserver(function () { requestAnimationFrame(function () { fades(); centre(); }); }).observe(list);
    if (wrap) wrap.addEventListener('svnty:docked', function (e) { if (e.detail && e.detail.docked) centre(); });
    /* the rail comes back with the bar: centre the current tab on every return, not only on the dock (E-17) */
    if (typeof MutationObserver === 'function') {
      var wasHidden = document.documentElement.classList.contains('svnty-bar-hidden');
      new MutationObserver(function () {
        var h = document.documentElement.classList.contains('svnty-bar-hidden');
        if (wasHidden && !h) centre();
        wasHidden = h;
      }).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    }
    window.addEventListener('load', function () { fades(); centre(); });
    fades();
    centre();
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-svnty-load-more-button]');
    if (!button) return;
    event.preventDefault();
    var wrapper = button.closest('[data-svnty-load-more]');
    if (wrapper && 'IntersectionObserver' in window) wrapper.dataset.auto = 'on';   /* a retry hands the block back to the scroll (E-16) */
    loadMore(button);
  });

  /* ---------------------------------------------------------------------
     R194 — THE VIEW TOGGLE (17:04: two systems). THE PHONE (under 750): two per row or one, html.svnty-view-one.
     THE IPAD AND THE DESKTOP: every column count in turn on html[data-svnty-cols] (1 to 3 on the iPad, 1 to 4 from 990).
     P17-26 (refuter R2 P1-2, THE STILL CARD over R194's glide and D6-1 F-10; 2026-09-30: "no animation on the product
     cards' pictures at all", desktop, iPad and iPhone): each step is INSTANT. The shopper keeps her place (the first card
     whose box crosses the chrome's bottom keeps its viewport y, one instant scrollBy in the same task as the apply), the
     rows are levelled inside the apply, nothing moves after the step lands, and nothing blanks: no card or picture is hidden,
     faded or re-shown, a painted picture keeps its file until the file for its new width is decoded (sizes). Arrow keys on
     the button step back and forth; a trackpad pinch (ctrl + wheel) over the grid steps; two fingers on the iPad or the
     phone too; a real haptic tick on the phone (the hidden switch).
     --------------------------------------------------------------------- */
  function viewToggle() {
    var btn = document.querySelector('[data-svnty-view]');
    if (!btn) return;
    var root = document.documentElement;
    var word = btn.querySelector('[data-svnty-view-word]');
    var PHONE = window.matchMedia('(max-width: 749px) and (orientation: portrait)');
    var LAND = window.matchMedia('(max-width: 989px) and (max-height: 500px) and (orientation: landscape)');
    var DESK = window.matchMedia('(min-width: 990px)');
    var rows = function () { return window.SVNTY && window.SVNTY.rows; };
    /* D6-1 (refuter F-27): a page without the boot snippet, or a root the boot could not reach, reads the memory here */
    try {
      if (PHONE.matches && !root.classList.contains('svnty-view-one') && localStorage.getItem('svnty:view') === 'one') root.classList.add('svnty-view-one');
      if (!PHONE.matches && !root.hasAttribute('data-svnty-cols')) { var c0 = parseInt(localStorage.getItem('svnty:cols') || '', 10); if (c0 >= 1 && c0 <= 4) root.setAttribute('data-svnty-cols', String(c0)); }
    } catch (err) { /* private mode */ }
    var TICK = (function () { var i = document.createElement('input'); i.type = 'checkbox'; i.setAttribute('switch', ''); return 'switch' in i; })();
    var sw = null;
    if (TICK) {
      var lab = document.createElement('label');
      lab.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);pointer-events:none;';
      sw = document.createElement('input'); sw.type = 'checkbox'; sw.setAttribute('switch', ''); sw.tabIndex = -1; sw.setAttribute('aria-hidden', 'true');
      lab.appendChild(sw); btn.parentNode.appendChild(lab);
    }
    function haptic() { if (sw) { try { sw.click(); } catch (err) { /* no tick */ } } }
    function grid() { return document.getElementById('product-grid'); }
    function items() { var g = grid(); return g ? Array.prototype.slice.call(g.querySelectorAll('.grid__item')) : []; }
    function maxCols() { return DESK.matches ? 4 : 3; }
    function cols() {
      if (PHONE.matches) return root.classList.contains('svnty-view-one') ? 1 : 2;
      var c = parseInt(root.getAttribute('data-svnty-cols') || '', 10);
      if (!(c >= 1)) c = maxCols();
      return Math.min(c, maxCols());
    }
    /* P17-26 (refuter R2 P1-2, no flash): a painted picture takes its new sizes only once the file those sizes pick is decoded.
       A detached probe with the same srcset and sizes picks the same file, so the swap on the card is a memory-cache hit: the
       old file paints until then, and complete / naturalWidth never drop for a frame. A picture not painted yet (lazy, off
       screen, hidden) takes its sizes at once, so no card off screen downloads anything. The newest request wins. */
    function setSizes(img, s) {
      if (!s || img.getAttribute('sizes') === s) return;
      var gen = img.__svntySz = (img.__svntySz || 0) + 1;
      var set = function () { if (img.__svntySz === gen) img.setAttribute('sizes', s); };
      if (!(img.complete && img.naturalWidth > 0 && img.getAttribute('srcset') && img.offsetWidth > 0)) { set(); return; }
      var p = new Image();
      p.sizes = s;
      p.srcset = img.getAttribute('srcset');
      if (typeof p.decode === 'function') p.decode().then(set, set);
      else { p.onload = set; p.onerror = set; }
    }
    function sizes() {
      var n = cols(), g = grid();
      if (!g) return;
      /* D6-1 (refuter F-13): the picture is asked at the card's rendered width, whatever the grid's rule at this width */
      var it = g.querySelector('.grid__item'), w = it ? Math.ceil(it.offsetWidth) : 0;   /* the layout width */
      var s = w > 0 ? w + 'px' : (PHONE.matches ? (n === 1 ? '100vw' : '50vw') : 'calc((min(100vw, 1200px) - 96px) / ' + n + ')');
      Array.prototype.forEach.call(g.querySelectorAll('.card__media img, .card img'), function (img) {
        if (!img.hasAttribute('data-svnty-sizes')) img.setAttribute('data-svnty-sizes', img.getAttribute('sizes') || '');
        setSizes(img, PHONE.matches && n === 2 ? img.getAttribute('data-svnty-sizes') : s);
      });
    }
    function reflect() {
      var n = cols();
      btn.setAttribute('data-n', String(n));
      var next = PHONE.matches ? (n === 2 ? 1 : 2) : (n === 1 ? maxCols() : n - 1);
      var wordN = function (k) { return (btn.getAttribute('data-word-n') || '').replace('{{ count }}', String(k)).replace('[count]', String(k)); };
      var now = PHONE.matches ? (n === 1 ? btn.getAttribute('data-word-one') : btn.getAttribute('data-word-two')) : wordN(n);
      var then = PHONE.matches ? (next === 1 ? btn.getAttribute('data-word-one') : btn.getAttribute('data-word-two')) : wordN(next);
      btn.setAttribute('aria-label', now || '');
      btn.setAttribute('title', then || '');
      if (word) word.textContent = PHONE.matches ? '' : String(n);
      sizes();
    }
    /* P17-26 (refuter R2 P1-2): the chrome's bottom = the header slab's, or the results block's under it (sticky: docked or
       in the flow right above the grid); the anchor is the first card whose box reaches below it, the card the shopper reads */
    function chromeBottom() {
      var b = 0, slab = document.querySelector('.svnty-headstack'), res = document.querySelector('[data-svnty-results]');
      if (slab) b = slab.getBoundingClientRect().bottom;
      if (res) b = Math.max(b, res.getBoundingClientRect().bottom);
      return b;
    }
    function anchor() {
      var line = chromeBottom(), list = items();
      for (var i = 0; i < list.length; i++) { var r = list[i].getBoundingClientRect(); if (r.height > 0 && r.bottom > line) return list[i]; }
      return null;
    }
    /* P17-26 (refuter R2 P1-2, THE STILL CARD): one task, no motion. The count changes, the rows are levelled (D6-1 F-11), and
       one instant scrollBy puts the anchor back on its y before the next frame paints. The engine's own scroll anchoring may
       already have moved the page inside the apply (WebKit did at 820); whoever moved it, the headstack re-bases on the new spot
       (its measure(), as after THE RESTORE), so the page's own jump never reads as the shopper's scroll and the bar stays as
       it was. No card is transformed or animated, so there is nothing to settle afterwards. */
    function flip(apply) {
      var r = rows(), a = anchor(), y0 = a ? a.getBoundingClientRect().top : 0, sy = window.scrollY;
      apply();
      if (r) r.measure();
      if (a && a.isConnected) {
        var dy = Math.round(a.getBoundingClientRect().top - y0);   /* WebKit scrolls by whole pixels: a rounded step keeps the anchor within 0.5 px */
        if (dy) window.scrollBy({ top: dy, left: 0, behavior: 'instant' });
      }
      var hs = window.__svntyHeadstack;
      if (window.scrollY !== sy && hs && typeof hs.measure === 'function') hs.measure();
    }
    function set(n, withTick) {
      if (LAND.matches) return;                           /* the rotated phone has its own grid (the landscape law), no toggle */
      var cur = cols();
      if (PHONE.matches) {
        var one = n === 1;
        if (one === (cur === 1)) return;
        if (withTick) haptic();
        try { localStorage.setItem('svnty:view', one ? 'one' : 'two'); } catch (err) { /* private mode */ }
        flip(function () { root.classList.toggle('svnty-view-one', one); });
      } else {
        n = Math.max(1, Math.min(maxCols(), n));
        if (n === cur) return;
        if (withTick) haptic();
        try { localStorage.setItem('svnty:cols', String(n)); } catch (err) { /* private mode */ }
        flip(function () { root.setAttribute('data-svnty-cols', String(n)); });
      }
      reflect();
    }
    function step(dir, wrap) {                            /* dir +1 = fewer per row (bigger), -1 = more per row */
      var n = cols();
      if (PHONE.matches) { set(dir > 0 ? 1 : 2, true); return; }
      var next = n - dir;
      if (wrap) { if (next < 1) next = maxCols(); if (next > maxCols()) next = 1; }
      else next = Math.max(1, Math.min(maxCols(), next));   /* D6-1 (refuter F-12, F-28): only the click wraps */
      set(next, true);
    }
    btn.addEventListener('click', function () { if (PHONE.matches) set(cols() === 2 ? 1 : 2, true); else step(1, true); });
    btn.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); if (PHONE.matches) set(1, true); else step(1, false); }
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); if (PHONE.matches) set(2, true); else step(-1, false); }
    });
    /* the trackpad pinch over the grid (ctrl + wheel is how the browser reports it): a step per 40 units in the pinch's
       direction, clamped at the ends, the run reset after 300 ms of quiet */
    var wheelAcc = 0, wheelAt = 0, wheelTimer = null;
    document.addEventListener('wheel', function (e) {
      if (!e.ctrlKey) return;
      var g = grid();
      if (!g || !g.contains(e.target)) return;
      e.preventDefault();
      wheelAcc += e.deltaY;
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(function () { wheelAcc = 0; }, 300);
      var now = Date.now();
      if (Math.abs(wheelAcc) >= 40 && now - wheelAt > 220) { step(wheelAcc < 0 ? 1 : -1, false); wheelAcc = 0; wheelAt = now; }
    }, { passive: false });
    /* two fingers over the grid: out = bigger pieces, in = more per row; one step per gesture, in the gesture's direction
       (D6-1, refuter F-12, F-34: passive listeners on the grid's container, never a blocking one on the document; the grid's
       touch-action keeps the page's zoom off the grid, which is the gesture's territory) */
    var d0 = 0, pinching = false, pinched = false;
    function dist(t) { var dx = t[0].clientX - t[1].clientX, dy = t[0].clientY - t[1].clientY; return Math.sqrt(dx * dx + dy * dy); }
    var gridBox = document.getElementById('ProductGridContainer') || document;
    gridBox.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 2) { pinching = false; return; }
      var g = grid();
      if (!g || !g.contains(e.target)) return;
      d0 = dist(e.touches); pinching = true; pinched = false;
    }, { passive: true });
    gridBox.addEventListener('touchmove', function (e) {
      if (!pinching || pinched || e.touches.length !== 2) return;
      var ratio = dist(e.touches) / (d0 || 1);
      if (ratio > 1.25) { pinched = true; if (PHONE.matches) set(1, true); else step(1, false); }
      else if (ratio < 0.8) { pinched = true; if (PHONE.matches) set(2, true); else step(-1, false); }
    }, { passive: true });
    gridBox.addEventListener('touchend', function () { pinching = false; }, { passive: true });
    gridBox.addEventListener('touchcancel', function () { pinching = false; }, { passive: true });
    /* a re-rendered grid (a filter, the next page) gets its pictures' sizes again */
    var container = document.getElementById('ProductGridContainer');
    if (container && typeof MutationObserver === 'function') new MutationObserver(function () { requestAnimationFrame(sizes); }).observe(container, { childList: true, subtree: true });
    /* D6-15 (refuter G-06, G-21): a resize or a rotation re-caps the RAW attribute (cols() already clamps, so the old guard
       `cols() > maxCols()` never fired and 1280 -> 838 kept four per row under the CSS) to the shopper's remembered count
       capped at this width's ceiling, and the button's state is re-read from the media queries' own change, a frame later,
       never from a resize that can land before the queries flip (the label, title, data-n and sizes lagged one step) */
    function recap() {
      if (!PHONE.matches && !LAND.matches) {
        var raw = parseInt(root.getAttribute('data-svnty-cols') || '', 10), want = maxCols();
        try { var mem = parseInt(localStorage.getItem('svnty:cols') || '', 10); if (mem >= 1 && mem <= 4) want = Math.min(mem, maxCols()); } catch (err) { /* private mode */ }
        if (!(raw >= 1) || raw > maxCols() || raw !== want) root.setAttribute('data-svnty-cols', String(want));
      }
      reflect();
    }
    var recapT = 0;
    function recapSoon() { if (recapT) return; recapT = requestAnimationFrame(function () { recapT = 0; recap(); }); }
    window.addEventListener('resize', recapSoon, { passive: true });
    [PHONE, LAND, DESK].forEach(function (mq) {
      if (mq.addEventListener) mq.addEventListener('change', recapSoon); else if (mq.addListener) mq.addListener(recapSoon);
    });
    if (!PHONE.matches && !root.hasAttribute('data-svnty-cols')) root.setAttribute('data-svnty-cols', String(maxCols()));
    reflect();
  }

  /* ---------------------------------------------------------------------
     D6-15 (refuter G-04) — THE NOUN LEAVES BY NEED. The collection's name leads the results bar and gives way first; the
     noun ("pieces") is redundant beside it, so it leaves whenever the name would ellipse beside it (data-tight on the
     block), at any width. Each check reads the untight state (the noun shown) in the same task, so the answer never
     oscillates; re-read when the block resizes, when facets.js rewrites the count, and when the fonts land.
     --------------------------------------------------------------------- */
  function resultsTight() {
    var wrap = document.querySelector('[data-svnty-results]');
    if (!wrap) return;
    var queued = 0;
    function check() {
      queued = 0;
      var name = wrap.querySelector('.svnty-results__name'), noun = wrap.querySelector('.svnty-results__noun');
      var was = wrap.hasAttribute('data-tight');
      if (!name || !noun) { if (was) wrap.removeAttribute('data-tight'); return; }
      if (was) wrap.removeAttribute('data-tight');
      var tight = name.scrollWidth > name.clientWidth;
      if (tight) wrap.setAttribute('data-tight', '');
    }
    function soon() { if (!queued) queued = requestAnimationFrame(check); }
    check();
    if (typeof ResizeObserver === 'function') new ResizeObserver(soon).observe(wrap);
    var count = document.getElementById('ProductCount');
    if (count && typeof MutationObserver === 'function') new MutationObserver(soon).observe(count, { childList: true, subtree: true, characterData: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(soon);
    window.addEventListener('load', soon);
  }

  /* D6-15 (refuter G-13): THE PAGE LOAD IS QUIET. The glyph's re-tiling and the block's step on the slab's beat are
     transitions only once the page is live: the first touch, click, key or scroll, or two frames after the load */
  function live() {
    var done = false;
    function on() {
      if (done) return;
      done = true;
      root.classList.add('svnty-cl-live');
      ['pointerdown', 'touchstart', 'keydown', 'wheel', 'scroll'].forEach(function (t) { window.removeEventListener(t, on, true); });
    }
    var root = document.documentElement;
    ['pointerdown', 'touchstart', 'keydown', 'wheel', 'scroll'].forEach(function (t) { window.addEventListener(t, on, { capture: true, passive: true }); });
    function later() { requestAnimationFrame(function () { requestAnimationFrame(on); }); }
    if (document.readyState === 'complete') later(); else window.addEventListener('load', later);
  }

  /* =====================================================================
     D8-6 THE COLOUR ADDRESS (R237, 2026-09-26 04:06, image 7: "wire literally all color swatches bubbles that we
     designed that are live on the website, such as the striped ones"). ?svnty_c=Navy|Blue:stripe narrows the page to the
     pieces carrying that swatch (the name; the kind too when stated). The store's facets know the taxonomy's ten families
     only, so the name is ours: THE COLOR MAP in the search sheet and the filter sheet's Color group write this address.
     The address rides a hidden field in every facet form (facets.js writes the URL from the forms, so a filter change
     would drop it), every page of the grid is loaded at once so the narrowing covers the whole collection, the count reads
     the visible cards (live counts only), an applied chip in the active row removes it, and a narrowing that leaves
     nothing says so under the grid.
     ===================================================================== */
  function colourAddress() {
    var container = document.getElementById('ProductGridContainer');
    if (!container) return;
    var narrowing = false, lastCount = null, loading = null, gridH = 0;

    function raw() { return new URLSearchParams(window.location.search).get('svnty_c') || ''; }
    function wanted(r) {
      return r.split('|').map(function (x) { return x.trim().toLowerCase(); }).filter(Boolean);
    }
    /* S-19 (lane SHEET_S): an entry is keyed by (name, kind): "blue:solid" matches the solid only, "blue:stripe" the stripe
       only; a bare "blue" (an address written before the kind was always stated) keeps meaning either kind */
    function cardHas(li, list) {
      var wrap = li.querySelector('[data-svnty-colours]');
      var mine = wrap ? String(wrap.getAttribute('data-svnty-colours') || '').toLowerCase() : '';
      if (!mine) {
        var sw = li.querySelectorAll('.svnty-card__sw[data-colour]');
        for (var i = 0; i < sw.length; i++) mine += '|' + String(sw[i].getAttribute('data-colour') || '').toLowerCase() + ':' + (sw[i].querySelector('.svnty-swv') ? 'stripe' : 'solid');
        if (mine) mine += '|';
      }
      for (var j = 0; j < list.length; j++) {
        var w = list[j];
        if (w.indexOf(':') > 0) { if (mine.indexOf('|' + w + '|') >= 0) return true; }
        else if (mine.indexOf('|' + w + ':') >= 0) return true;
      }
      return false;
    }
    function fields(r) {
      var forms = document.querySelectorAll('form[id^="FacetFiltersForm"], form[id^="FacetSortForm"]');
      Array.prototype.forEach.call(forms, function (form) {
        var f = form.querySelector('input[name="svnty_c"]');
        if (r) {
          if (!f) { f = document.createElement('input'); f.type = 'hidden'; f.name = 'svnty_c'; form.appendChild(f); }
          if (f.value !== r) f.value = r;
        } else if (f) f.parentNode.removeChild(f);
      });
    }
    function wrapper() { return document.querySelector('[data-svnty-load-more]'); }
    function pagesLeft() {
      var w = wrapper();
      if (!w) return false;
      var next = parseInt(w.dataset.nextPage, 10), pages = parseInt(w.dataset.totalPages, 10);
      return isFinite(next) && isFinite(pages) && next <= pages;
    }
    /* S-21 (lane SHEET_S): every page left is fetched at once and appended in ONE write, in page order; the narrowing then
       counts the whole set in one frame (the page by page chain wrote a count on every page: "0 pieces", "1 piece", "2") */
    function loadRest() {
      var w = wrapper(), grid = document.getElementById('product-grid');
      if (!w || !grid || w.dataset.busy === 'true') return loading;
      var next = parseInt(w.dataset.nextPage, 10), pages = parseInt(w.dataset.totalPages, 10), sectionId = w.dataset.sectionId;
      if (!sectionId || !isFinite(next) || !isFinite(pages) || next > pages) return null;
      var params = new URLSearchParams(window.location.search);
      params.delete('section_id');
      var asks = [];
      for (var n = next; n <= pages; n++) {
        params.set('page', String(n));
        asks.push(fetch(window.location.pathname + '?' + params.toString() + '&section_id=' + encodeURIComponent(sectionId), { credentials: 'same-origin' })
          .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); }));
      }
      w.dataset.busy = 'true';
      var mine = loading = Promise.all(asks).then(function (texts) {
        if (loading !== mine || !grid.isConnected) return;
        var list = listIn(grid) || grid, added = 0, frag = document.createDocumentFragment();
        texts.forEach(function (html) {
          cardsIn(new DOMParser().parseFromString(html, 'text/html').getElementById('product-grid')).forEach(function (item) {
            item.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
            item.removeAttribute('data-cascade');
            item.removeAttribute('style');
            item.hidden = true;
            frag.appendChild(item);
            added++;
          });
        });
        list.appendChild(frag);
        w.dataset.shown = String((parseInt(w.dataset.shown, 10) || 0) + added);
        w.dataset.nextPage = String(pages + 1);
        var button = w.querySelector('[data-svnty-load-more-button]');
        if (button) finish(button, w.querySelector('[data-svnty-load-more-done]'), parseInt(w.dataset.total, 10) || 0);
      }).catch(function () { /* the button stays as the retry: the auto loader takes the page as it always did */ })
        .then(function () {
          if (loading !== mine) return;
          loading = null;
          w.dataset.busy = 'false';
          guarded();
        });
      return loading;
    }
    /* ONE COUNT SOURCE (S-21): the bar's count, the results head's line, the filter sheet's count (which its SHOW button
       mirrors) and the grid's foot all print the narrowed number, written together */
    function countIn(el, n) {
      if (!el) return;
      var num = el.querySelector('.svnty-results__n') || el;
      var text = num.textContent.replace(/\d[\d,]*/, String(n));
      if (num.textContent !== text) num.textContent = text;
      var noun = el.querySelector('.svnty-results__noun');
      var nouns = noun ? [noun] : [el];
      nouns.forEach(function (x) {
        var t = n === 1 ? x.textContent.replace(/\bpieces\b/, 'piece').replace(/\bresults\b/, 'result') : x.textContent.replace(/\bpiece\b(?!s)/, 'pieces').replace(/\bresult\b(?!s)/, 'results');
        if (x.textContent !== t) x.textContent = t;
      });
    }
    function count(n) {
      countIn(document.getElementById('ProductCount'), n);
      countIn(document.getElementById('ProductCountDesktop'), n);
      Array.prototype.forEach.call(document.querySelectorAll('.mobile-facets__count, .svnty-search-head__count'), function (el) { countIn(el, n); });
      var done = document.querySelector('[data-svnty-load-more-done]');
      /* CL24 (GUARDS_Part8): the foot lives inside the watched container, so it is written only when it changes (a text write
         swaps the text node, the observer fires, narrow() runs again: a loop that froze the page once every page was in) */
      if (done && !pagesLeft()) { var all = fill('all_shown', { '[total]': n }); if (done.textContent !== all) done.textContent = all; if (done.hidden !== (n === 0)) done.hidden = n === 0; }
      state(n, false);
      try { document.dispatchEvent(new CustomEvent('svnty:colour:count', { detail: { n: n } })); } catch (e) {}
    }
    function words(r) {
      return r.split('|').map(function (x) {
        var parts = x.trim().split(':');
        return parts[0] + (parts[1] === 'stripe' ? ' stripe' : '');
      }).filter(Boolean).join(' + ');
    }
    function chip(r) {
      var hosts = document.querySelectorAll('.active-facets');
      Array.prototype.forEach.call(hosts, function (host) {
        var old = host.querySelector('[data-svnty-colour-chip]');
        if (!r) { if (old) old.parentNode.removeChild(old); return; }
        var u = new URL(window.location.href); u.searchParams.delete('svnty_c');
        if (old) { var oa = old.querySelector('a'), ow = old.querySelector('[data-svnty-colour-words]'); if (oa.href !== u.href) oa.href = u.href; if (ow.textContent !== words(r)) ow.textContent = words(r); return; }
        /* the chip is inserted as markup whole: Trade's <facet-remove> binds its anchor in its constructor, so the anchor
           must exist when the element upgrades (a bare createElement left the anchor unbound and the tap navigated) */
        var sheet = host.classList.contains('svnty-fs__applied'), safeHref = u.href.replace(/"/g, '&quot;'), clearWord = String(STR.clear || 'Clear').replace(/</g, '&lt;');
        var html = sheet
          ? '<facet-remove class="svnty-fs__chipwrap" role="listitem" data-svnty-colour-chip><a href="' + safeHref + '" class="svnty-fs__chip"><span data-svnty-colour-words></span>' + ((window.SVNTY && SVNTY.iconHTML) ? SVNTY.iconHTML('close', 10, 1.08) : '') + '<span class="visually-hidden">' + clearWord + '</span></a></facet-remove>'
          : '<facet-remove data-svnty-colour-chip><a href="' + safeHref + '" class="active-facets__button active-facets__button--light"><span class="active-facets__button-inner button button--tertiary"><span data-svnty-colour-words></span><span class="svg-wrapper"><svg class="icon icon-close-small" aria-hidden="true" focusable="false"><use href="#icon-close-small"></use></svg></span><span class="visually-hidden">' + clearWord + '</span></span></a></facet-remove>';
        host.insertAdjacentHTML('beforeend', html);
        var made = host.querySelector('[data-svnty-colour-chip]:last-of-type');
        if (made) made.querySelector('[data-svnty-colour-words]').textContent = words(r);
      });
    }
    function empty(on) {
      var grid = document.getElementById('product-grid');
      var host = grid && grid.parentNode;
      if (!host) return;
      var line = host.querySelector('[data-svnty-colour-empty]');
      if (!on) { if (line) line.parentNode.removeChild(line); return; }
      if (line) return;
      line = document.createElement('p');
      line.setAttribute('data-svnty-colour-empty', '');
      line.className = 'svnty-colour-empty';
      line.textContent = STR.colour_empty || 'Nothing in this color with these filters.';
      host.appendChild(line);
    }
    /* THE HOLD (S-21): while an address is set and pages remain, the grid's new cards stay out of sight (the grid keeps the
       height it had, the load more block its spinner) and the count keeps the last narrowed number, or none on arrival;
       nothing says "0 pieces" or "Nothing in this color" before the last page is in */
    /* the state the filter sheet's SHOW button reads (svnty-filter.js mirrorCount): the narrowed count, or pending */
    function state(n, pending) {
      var SV = window.SVNTY = window.SVNTY || {};
      SV.colourState = n === undefined ? null : { n: n, pending: !!pending };
    }
    function hold(on) {
      var grid = document.getElementById('product-grid');
      if (on) state(lastCount, true);
      container.classList.toggle('svnty-colour-pending', on);
      document.documentElement.classList.toggle('svnty-colour-wait', on && lastCount === null);
      if (grid) grid.style.minHeight = on && gridH ? gridH + 'px' : '';
    }
    function narrow() {
      var r = raw(), list = wanted(r);
      fields(r);
      var grid = document.getElementById('product-grid');
      var items = grid ? cardsIn(grid) : [];
      if (!list.length) {
        if (narrowing) { items.forEach(function (li) { li.hidden = false; }); narrowing = false; lastCount = null; hold(false); state(); chip(''); empty(false); }
        return;
      }
      narrowing = true;
      chip(r);
      if (pagesLeft()) {
        if (grid && !container.classList.contains('svnty-colour-pending')) { var h = grid.getBoundingClientRect().height; gridH = lastCount === null ? 0 : Math.round(h); }
        hold(true);
        if (lastCount !== null) count(lastCount);
        empty(false);
        loadRest();
        return;
      }
      var shown = 0;
      items.forEach(function (li) { var ok = cardHas(li, list); if (li.hidden !== !ok) li.hidden = !ok; if (ok) shown++; });
      count(shown);
      empty(shown === 0 && items.length > 0);
      hold(false);
      if (shown !== lastCount) { lastCount = shown; if (window.SVNTY && window.SVNTY.rows) window.SVNTY.rows.measure(); }
    }
    narrow();
    /* the grid's re-render (facets.js) is judged in the same microtask, before the new cards can paint unnarrowed */
    /* narrow() never answers its own writes: the observer stands down while it runs and takes the records it made */
    var mo = typeof MutationObserver === 'function' ? new MutationObserver(function () { if (raw() || narrowing) guarded(); }) : null;
    function guarded() { if (mo) mo.disconnect(); try { narrow(); } finally { if (mo) { mo.takeRecords(); mo.observe(container, { childList: true, subtree: true }); } } }
    if (mo) mo.observe(container, { childList: true, subtree: true });
    window.addEventListener('popstate', function () { requestAnimationFrame(guarded); });
    document.addEventListener('svnty:loadmore', function () { if (raw()) guarded(); });
  }

  function boot() {
    keepHeaderHeightTrue();
    live();
    viewToggle();
    resultsTight();
    typeRail();
    document.addEventListener('shopify:section:load', typeRail);
    resultsDock();
    tabsRail();
    autoLoad();
    colourAddress();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
